import React, { useEffect, useMemo, useState } from 'react';
import { Icon, Button } from '@tviklax/ui-components';
import styled from 'styled-components';
import type { OrderFormData, OrderItem } from './types';

const INCOTERMS = ['EXW', 'FOB', 'FCA', 'CPT', 'CIP', 'CIF', 'CFR', 'DAP', 'DDP'];
const CURRENCIES = ['USD', 'INR', 'EUR', 'GBP', 'AED'];

const SplitLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[6]};
  align-items: stretch;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const ViewerColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  min-width: 0;
`;

const ViewerFrame = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.gray100};
  height: 640px;
  display: flex;
  align-items: center;
  justify-content: center;

  iframe, img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border: none;
  }
`;

const ViewerEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => theme.colors.gray500};
  font-size: 13px;
`;

const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
  min-width: 0;
`;

const HeaderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing[4]};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray500};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const FieldInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray800};
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}15;
  }
`;

const FieldSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray800};
  background: white;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ItemsHeading = styled.h4`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray800};
`;

const TableScroll = styled.div`
  overflow: auto;
  max-height: 420px;
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

const ItemsTable = styled.table`
  width: 100%;
  min-width: 640px;
  border-collapse: collapse;
  table-layout: fixed;

  col.sNo { width: 6%; }
  col.hsCode { width: 13%; }
  col.description { width: 29%; }
  col.qty { width: 10%; }
  col.unitPrice { width: 14%; }
  col.taxableValue { width: 14%; }
  col.lineTotal { width: 14%; }
`;

const Th = styled.th`
  text-align: left;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${({ theme }) => theme.colors.gray500};
  padding: 10px 12px;
  background: ${({ theme }) => theme.colors.gray100};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
  position: sticky;
  top: 0;
  z-index: 1;
`;

const Td = styled.td`
  padding: 6px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray100};
`;

const CellInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray800};
  outline: none;
  background: transparent;
  text-overflow: ellipsis;

  &:hover, &:focus {
    border-color: ${({ theme }) => theme.colors.gray300};
    background: white;
  }
`;

const LineTotalValue = styled.div`
  padding: 6px 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray800};
  text-align: right;
`;

const SNoValue = styled.div`
  padding: 6px 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray500};
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  padding-top: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.gray200};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    button {
      width: 100%;
    }
  }
`;

interface ReviewLineItem {
  id: number;
  description: string;
  hsCode: string;
  quantity: number;
  unitPrice: number;
  taxableValue: number;
}

interface ImportInvoiceReviewProps {
  data: Partial<OrderFormData> & { confidence?: Record<string, number> };
  file: File | null;
  onConfirm: (data: Partial<OrderFormData>) => void;
  onEdit?: () => void;
  secondaryActionLabel?: string;
}

const toReviewItems = (items?: OrderItem[]): ReviewLineItem[] => {
  if (!items || items.length === 0) return [];
  return items.map((item, index) => ({
    id: index + 1,
    description: item.productName || item.details || '',
    hsCode: item.hsCode || '',
    quantity: Number(item.quantity) || 0,
    unitPrice: Number(item.unitPrice) || 0,
    taxableValue: Number(item.declaredValue ?? item.total) || 0,
  }));
};

export const ImportInvoiceReview: React.FC<ImportInvoiceReviewProps> = ({
  data,
  file,
  onConfirm,
  onEdit,
  secondaryActionLabel,
}) => {
  const [invoiceNumber, setInvoiceNumber] = useState(data.invoiceNumber || '');
  const [documentDate, setDocumentDate] = useState(data.deliveryDate || '');
  const [currency, setCurrency] = useState(data.currency || 'USD');
  const [incoterms, setIncoterms] = useState(data.incoterms || 'FOB');
  const [items, setItems] = useState<ReviewLineItem[]>(() => toReviewItems(data.items));

  const objectUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (objectUrl) URL.revokeObjectURL(objectUrl); }, [objectUrl]);

  const isImage = file ? /^image\//.test(file.type) : false;

  const updateItem = (id: number, patch: Partial<ReviewLineItem>) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  };

  const handleConfirm = () => {
    const mappedItems: OrderItem[] = items.map((item, index) => {
      const lineTotal = Number((item.quantity * item.unitPrice).toFixed(2));
      return {
        itemno: index + 1,
        productId: '',
        productName: item.description || `Item ${index + 1}`,
        details: item.description,
        unit: 'pcs',
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        price: item.unitPrice,
        total: lineTotal,
        subtotal: lineTotal,
        hsCode: item.hsCode,
        declaredValue: item.taxableValue,
        countryOfOrigin: '',
        exportControlFlag: '',
        ecgcEligibility: 'Pending check',
      };
    });

    const subtotal = mappedItems.reduce((sum, item) => sum + (item.total || 0), 0);

    onConfirm({
      ...data,
      invoiceNumber,
      deliveryDate: documentDate,
      currency,
      incoterms,
      items: mappedItems,
      subtotal,
      total: subtotal,
    });
  };

  return (
    <SplitLayout>
      <ViewerColumn>
        <ItemsHeading>Source Document</ItemsHeading>
        <ViewerFrame>
          {objectUrl && file?.type === 'application/pdf' && (
            <iframe title="Uploaded document" src={`${objectUrl}#toolbar=0&navpanes=0&scrollbar=0`} />
          )}
          {objectUrl && isImage && (
            <img src={objectUrl} alt="Uploaded document" />
          )}
          {!objectUrl && (
            <ViewerEmpty>
              <Icon name="FileText" size={32} color="#94a3b8" />
              Document preview unavailable
            </ViewerEmpty>
          )}
        </ViewerFrame>
      </ViewerColumn>

      <FormColumn>
        <ItemsHeading>Parsed Order Details</ItemsHeading>
        <HeaderGrid>
          <Field>
            <FieldLabel>PO / Invoice Number</FieldLabel>
            <FieldInput value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
          </Field>
          <Field>
            <FieldLabel>Document Date</FieldLabel>
            <FieldInput type="date" value={documentDate} onChange={(e) => setDocumentDate(e.target.value)} />
          </Field>
          <Field>
            <FieldLabel>Currency</FieldLabel>
            <FieldSelect value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </FieldSelect>
          </Field>
          <Field>
            <FieldLabel>Incoterm</FieldLabel>
            <FieldSelect value={incoterms} onChange={(e) => setIncoterms(e.target.value)}>
              {INCOTERMS.map((term) => <option key={term} value={term}>{term}</option>)}
            </FieldSelect>
          </Field>
        </HeaderGrid>

        <div>
          <ItemsHeading style={{ marginBottom: 8 }}>Line Items</ItemsHeading>
          <TableScroll>
            <ItemsTable>
              <colgroup>
                <col className="sNo" />
                <col className="hsCode" />
                <col className="description" />
                <col className="qty" />
                <col className="unitPrice" />
                <col className="taxableValue" />
                <col className="lineTotal" />
              </colgroup>
              <thead>
                <tr>
                  <Th>S.No</Th>
                  <Th>HSN / HS Code</Th>
                  <Th>Description</Th>
                  <Th>Qty</Th>
                  <Th>Unit Price</Th>
                  <Th>Taxable Value</Th>
                  <Th style={{ textAlign: 'right' }}>Line Total</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={item.id}>
                    <Td><SNoValue>{index + 1}</SNoValue></Td>
                    <Td>
                      <CellInput value={item.hsCode} onChange={(e) => updateItem(item.id, { hsCode: e.target.value })} />
                    </Td>
                    <Td>
                      <CellInput
                        value={item.description}
                        title={item.description}
                        onChange={(e) => updateItem(item.id, { description: e.target.value })}
                      />
                    </Td>
                    <Td>
                      <CellInput
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(item.id, { quantity: Number(e.target.value) || 0 })}
                      />
                    </Td>
                    <Td>
                      <CellInput
                        type="number"
                        value={item.unitPrice}
                        onChange={(e) => updateItem(item.id, { unitPrice: Number(e.target.value) || 0 })}
                      />
                    </Td>
                    <Td>
                      <CellInput
                        type="number"
                        value={item.taxableValue}
                        onChange={(e) => updateItem(item.id, { taxableValue: Number(e.target.value) || 0 })}
                      />
                    </Td>
                    <Td>
                      <LineTotalValue>
                        {(item.quantity * item.unitPrice).toFixed(2)}
                      </LineTotalValue>
                    </Td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <Td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: '20px 12px' }}>
                      No line items were extracted from this document.
                    </Td>
                  </tr>
                )}
              </tbody>
            </ItemsTable>
          </TableScroll>
        </div>

        <ActionBar>
          {onEdit && (
            <Button variant="outline" onClick={onEdit}>
              {secondaryActionLabel || 'Route to Exception Queue'}
            </Button>
          )}
          <Button onClick={handleConfirm}>
            Confirm Data & Generate Logistics Order
          </Button>
        </ActionBar>
      </FormColumn>
    </SplitLayout>
  );
};
